import { restoreFirstSessionRequestAtom } from '@heidi/session';
import { sessionListScopeAtom } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

/**
 * Sticky last non-empty `sessionId` for the list row highlight. Survives the URL
 * id clearing on back-nav; reset on scope change and when the session it points
 * at is deleted (so the mount-time auto-restore can't re-open a dead id).
 */
export const useLastViewedSessionId = (sessionId: string): string => {
  const [lastViewedSessionId, setLastViewedSessionId] = useState('');
  // Conditional setState in render avoids the one-frame highlight gap a
  // `useEffect` would introduce.
  if (sessionId && sessionId !== lastViewedSessionId) {
    setLastViewedSessionId(sessionId);
  }

  // Reset on a scope CHANGE only. Running the reset on mount too would clear
  // the id the render above just latched and schedule a second pass of the
  // whole page (a changed context value) to latch it again.
  const scope = useAtomValue(sessionListScopeAtom);
  const prevScopeRef = useRef(scope);
  useEffect(() => {
    if (scope === prevScopeRef.current) return;
    prevScopeRef.current = scope;
    setLastViewedSessionId('');
  }, [scope]);

  // Read the current id via a ref so resetting it below doesn't re-fire this
  // effect (the nonce guard would short-circuit the re-run anyway, but keeping
  // it out of the deps avoids the wasted cycle).
  const lastViewedSessionIdRef = useRef(lastViewedSessionId);
  lastViewedSessionIdRef.current = lastViewedSessionId;

  const { nonce: deletedRestoreNonce, excludeSessionIds: justDeletedSessionIds } =
    useAtomValue(restoreFirstSessionRequestAtom);
  const prevDeletedRestoreNonceRef = useRef(deletedRestoreNonce);
  useEffect(() => {
    if (deletedRestoreNonce === prevDeletedRestoreNonceRef.current) return;
    prevDeletedRestoreNonceRef.current = deletedRestoreNonce;
    const current = lastViewedSessionIdRef.current;
    if (current && justDeletedSessionIds.includes(current)) {
      setLastViewedSessionId('');
    }
  }, [deletedRestoreNonce, justDeletedSessionIds]);

  return lastViewedSessionId;
};
